import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, PhoneIcon, MessageSquareIcon, MailIcon, CheckIcon, PlayIcon, RefreshCwIcon, LinkIcon, ArrowRightIcon, BanknoteIcon } from 'lucide-react';
import { Order, OrderStatus, CANCEL_REASONS, CUSTOMER_STATUS, PAYMENT_METHOD_LABEL, methodOf, paymentOutstanding, money } from '../../data/orders';
import { StatusChip, PaymentChip } from './OrderChips';
interface OrderDetailDrawerProps {
  order: Order;
  onClose: () => void;
  onStatus: (status: OrderStatus) => void;
  onCancel: (reason: string, note: string) => void;
  /** Recording cash collected by hand, separately from fulfilment. */
  onMarkPaid?: () => void;
}

/**
 * A side drawer rather than a page: the merchant keeps their place in the list
 * while working through an order, which is how a busy front desk actually
 * operates.
 */
export function OrderDetailDrawer({
  order,
  onClose,
  onStatus,
  onCancel,
  onMarkPaid
}: OrderDetailDrawerProps) {
  const [confirmFulfil, setConfirmFulfil] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState(CANCEL_REASONS[0]);
  const [note, setNote] = useState('');
  const external = order.managedBy === 'connected';
  return <div className="fixed inset-0 z-[70] flex justify-end" data-id="element-3006" data-mp="geyn1ekltvfz">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.18,
      ease: 'easeOut'
    }} onClick={onClose} className="absolute inset-0 bg-dark-navy/30" data-id="element-3007" data-mp="ggjh7330bcdb" />
      <motion.aside initial={{
      x: 32,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: 32,
      opacity: 0
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} role="dialog" aria-label={`Order ${order.id}`} className="relative w-full max-w-[560px] h-full bg-light-bg flex flex-col shadow-large" data-id="element-3008" data-mp="zemc1yi8k3j1">
        {/* Header */}
        <header className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-5" data-id="element-3009" data-mp="3337iatqd1284">
          <div className="flex items-start justify-between gap-4" data-id="element-3010" data-mp="2y3bf2wphletm">
            <div className="min-w-0" data-id="element-3011" data-mp="2y67c8itifzh0">
              <div className="flex items-center gap-2.5 flex-wrap" data-id="element-3012" data-mp="3c2u9yntaetnz">
                <h2 className="font-display font-bold text-xl text-dark-navy" data-id="element-3013" data-mp="3820m1ufuhev3">
                  Order #{order.id}
                </h2>
                <StatusChip status={order.status} size="lg" data-id="element-3014" data-mp="3ltozq25qsbe6" />
              </div>
              <p className="text-sm text-slate-500 mt-1.5" data-id="element-3015" data-mp="3gtu5ppqo75ow">
                {order.customer.name} · {order.placed}
              </p>
            </div>
            <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 -mt-1 rounded-xl text-slate-400 hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-3016" data-mp="3vruxq8ofgxdr">
              <XIcon className="w-5 h-5" data-id="element-3017" data-mp="ia9ctx2f4ttz" />
            </button>
          </div>

          <div className="flex items-end justify-between gap-4 mt-5" data-id="element-3018" data-mp="i86ix1y5agjz">
            <div data-id="element-3019" data-mp="4ahzzznn1jhk">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-3020" data-mp="4d46rx2pfuyq">
                Total
              </p>
              <p className="font-display font-bold text-2xl text-dark-navy mt-0.5" data-id="element-3021" data-mp="3qpsx46l431ww">
                {money(order.total)}
              </p>
            </div>
            <div className="text-right" data-id="element-3022" data-mp="3vj5m4htpdaq6">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-3023" data-mp="3hq66mftx7gim">
                Payment
              </p>
              <div className="mt-1.5" data-id="element-3024" data-mp="3mpir4txbkbi1">
                <PaymentChip payment={order.payment} data-id="element-3025" data-mp="37rgocly43u1u" />
              </div>
              <p className="text-[11px] font-semibold text-slate-400 mt-1.5" data-id="element-3026" data-mp="37s8j5ujvxnr2">
                {PAYMENT_METHOD_LABEL[methodOf(order)]}
              </p>
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4" data-id="element-3027" data-mp="jxw5m5dfxhes">
          {/* What the customer is seeing right now */}
          <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200" data-id="element-3028" data-mp="a5q9cjg5odts">
            <p className="text-xs text-slate-500" data-id="element-3029" data-mp="57ykjyat57tt">
              Your customer currently sees
            </p>
            <span className="ml-auto text-xs font-bold text-dark-navy" data-id="element-3030" data-mp="953jvwbayow">
              {CUSTOMER_STATUS[order.status]}
            </span>
          </div>

          {/* Cash is collected by hand, so it is recorded by hand — and stays
              independent of whether the order has been fulfilled. */}
          {methodOf(order) === 'cash' && order.payment !== 'refunded' && <div className={`px-4 py-4 rounded-2xl border ${paymentOutstanding(order) ? 'bg-amber-50/70 border-amber-200/70' : 'bg-white border-slate-200'}`} data-id="element-3031" data-mp="1llyfeuusq7a7">
              <div className="flex items-start gap-3" data-id="element-3032" data-mp="1o64jd2jetxqr">
                <span className="w-9 h-9 rounded-xl bg-white border border-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-3033" data-mp="1o5l2zq9erheu">
                  <BanknoteIcon className={`w-4 h-4 ${paymentOutstanding(order) ? 'text-amber-600' : 'text-slate-500'}`} strokeWidth={1.75} data-id="element-3034" data-mp="1xvo8ur1mopul" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-3035" data-mp="157sc5mc26xxt">
                  <p className="text-sm font-bold text-dark-navy" data-id="element-3036" data-mp="1593fxm5i85ox">
                    Cash / Pay on Delivery
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed" data-id="element-3037" data-mp="1f5g64a1uc1ex">
                    {order.payment === 'paid' ? `Collected${order.paidBy ? ` by ${order.paidBy}` : ''}${order.paidAt ? ` · ${order.paidAt}` : ''}.` : paymentOutstanding(order) ? 'This order is complete but the payment has not been recorded yet.' : 'You collect this payment when the order is handed over.'}
                  </p>
                </div>
                {order.payment !== 'paid' && onMarkPaid && <button onClick={onMarkPaid} className="flex-shrink-0 px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-dark-navy hover:border-primary-indigo/40 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3038" data-mp="14hv7ckbrfasf">
                    Mark as paid
                  </button>}
              </div>
            </div>}

          {/* Items */}
          <Block title="Order" data-id="element-3039" data-mp="lkaihd8zwc7t">
            <div className="space-y-3" data-id="element-3040" data-mp="gsapy5v98ll9">
              {order.lines.map((l, i) => <div key={i} className="flex items-center gap-3.5" data-id="element-3041" data-mp="gqg2ajslmfdh" data-mpi={i}>
                  <img src={l.image} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0 bg-slate-100" data-id="element-3042" data-mp="3p6j96nbw000r" />
                  <div className="min-w-0 flex-1" data-id="element-3043" data-mp="3b0zfte3u0yyw">
                    <p className="text-sm font-bold text-dark-navy truncate" data-id="element-3044" data-mp="3ft0oyr5pe9kp">
                      {l.name}
                    </p>
                    {l.variant && <p className="text-xs text-slate-500 mt-0.5 truncate" data-id="element-3045" data-mp="3129xxlbxrein">
                        {l.variant}
                      </p>}
                  </div>
                  <p className="text-sm text-slate-600 flex-shrink-0" data-id="element-3046" data-mp="3ttlavw6x0lif">
                    {l.qty} × {money(l.price)}
                  </p>
                </div>)}
            </div>
            <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100" data-id="element-3047" data-mp="3jucfcyweiyty">
              <p className="text-sm font-semibold text-slate-500" data-id="element-3048" data-mp="3os48fw8s8qzq">Total</p>
              <p className="font-display font-bold text-lg text-dark-navy" data-id="element-3049" data-mp="28enwojq6klyy">
                {money(order.total)}
              </p>
            </div>
          </Block>

          {/* Customer */}
          <Block title="Customer" data-id="element-3050" data-mp="1upao2tj5a1q7">
            <p className="text-sm font-bold text-dark-navy" data-id="element-3051" data-mp="1upkdog0a26vd">
              {order.customer.name}
            </p>
            <dl className="mt-3 space-y-1.5" data-id="element-3052" data-mp="1priyi65z2rp6">
              <Row label="Phone" value={order.customer.phone} data-id="element-3053" data-mp="1kr4nqgczl3ng" />
              {order.customer.whatsapp && <Row label="WhatsApp" value={order.customer.whatsapp} data-id="element-3054" data-mp="1gpt6oz86hqqv" />}
              {order.customer.email && <Row label="Email" value={order.customer.email} data-id="element-3055" data-mp="2dgv3etu9odlo" />}
            </dl>
            <div className="flex flex-wrap items-center gap-2 mt-4" data-id="element-3056" data-mp="28hqwyayq8tz1">
              <Action icon={MessageSquareIcon} label="Message customer" data-id="element-3057" data-mp="23nmnnl1zo9us" />
              <Action icon={PhoneIcon} label="Call" data-id="element-3058" data-mp="1znvrf1tp11iv" />
              <Action icon={MailIcon} label="WhatsApp" data-id="element-3059" data-mp="1zl9k93apso16" />
            </div>
            {order.customer.conversation && <button className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-3060" data-mp="2rejrsbnjm2vh">
                View conversation
                <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3061" data-mp="iup4wgny7jhi" />
              </button>}
          </Block>

          {/* Fulfilment facts — only the ones that apply */}
          {order.details.length > 0 && <Block title="Fulfilment" data-id="element-3062" data-mp="rvx4r3644sdp">
              <dl className="space-y-1.5" data-id="element-3063" data-mp="rwyff6j7vcuw">
                {order.details.map(d => <Row key={d.label} label={d.label} value={d.value} data-id="element-3064" data-mp="wu8dtbol2yuv" />)}
              </dl>
              {order.instructions && <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-100" data-id="element-3065" data-mp="16ktagtkp5tb5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-3066" data-mp="1am5192fb4kkx">
                    Special instructions
                  </p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed" data-id="element-3067" data-mp="1fmstfrf9v7gq">
                    {order.instructions}
                  </p>
                </div>}
            </Block>}

          {/* Where the work happens */}
          <Block title="Handling" data-id="element-3068" data-mp="8lg8yhtm1h1q">
            <div className="flex items-start gap-3" data-id="element-3069" data-mp="8mhmqu20ghn5">
              <span className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0" data-id="element-3070" data-mp="cnbb1ojd7ygk">
                {external ? <LinkIcon className="w-4 h-4 text-slate-500" strokeWidth={1.75} data-id="element-3071" data-mp="hetc0cle1z1j" /> : <CheckIcon className="w-4 h-4 text-slate-500" data-id="element-3072" data-mp="rbxotltcgi5o" />}
              </span>
              <div className="min-w-0" data-id="element-3073" data-mp="war33ggggd6w">
                <p className="text-sm font-bold text-dark-navy" data-id="element-3074" data-mp="wbsfppd4d7qq">
                  {external ? 'Connected system' : 'Managed in Talab'}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed" data-id="element-3075" data-mp="2y2x7clf1y8b2">
                  {external ? 'This order is processed by your own system. Its status updates here automatically.' : 'You process this order here, in Talab.'}
                </p>
                {external && <dl className="mt-3 space-y-1.5" data-id="element-3076" data-mp="2pt7v2hmpef1f">
                    <Row label="External order ID" value={order.externalId!} data-id="element-3077" data-mp="2kuwmsbc24clb" />
                    <Row label="Last updated" value={order.lastSync!} data-id="element-3078" data-mp="2kstz3xydmvjr" />
                  </dl>}
              </div>
            </div>
            <p className="text-[11px] font-semibold text-slate-400 mt-4 pt-3 border-t border-slate-100" data-id="element-3079" data-mp="3dju2q7sspxaw">
              Came from {order.source}
            </p>
          </Block>

          {/* Timeline */}
          <Block title="Timeline" data-id="element-3080" data-mp="34i49qxj9mqb0">
            <ol className="space-y-0" data-id="element-3081" data-mp="34h2qi5s3ixi7">
              {order.timeline.map((e, i) => <li key={i} className="flex gap-3" data-id="element-3082" data-mp="2zjaw953zazau" data-mpi={i}>
                  <div className="flex flex-col items-center flex-shrink-0" data-id="element-3083" data-mp="1t3gy8bqxwfsg">
                    <span className="w-2 h-2 rounded-full bg-primary-indigo/40 mt-1.5" data-id="element-3084" data-mp="1t1e3xfir80hx" />
                    {i < order.timeline.length - 1 && <span className="w-px flex-1 bg-slate-200 my-1" data-id="element-3085" data-mp="1pmuq1a0zved9" />}
                  </div>
                  <div className="pb-4 min-w-0" data-id="element-3086" data-mp="1uke1y3a7zf21">
                    <p className="text-sm font-semibold text-dark-navy" data-id="element-3087" data-mp="1glentpkc2ik4">
                      {e.label}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5" data-id="element-3088" data-mp="1klou73c1fazi">
                      {e.time}
                      {e.by ? ` · ${e.by}` : ''}
                    </p>
                  </div>
                </li>)}
            </ol>
          </Block>

          {order.status === 'cancelled' && order.cancelReason && <p className="text-xs text-slate-500 px-1" data-id="element-3089" data-mp="wyytc5bfdmgh">
              Cancelled — {order.cancelReason.toLowerCase()}.
            </p>}
        </div>

        {/* Action bar — one obvious next step */}
        <footer className="flex-shrink-0 bg-white border-t border-slate-200 px-6 py-4" data-id="element-3090" data-mp="nyk4wx35zr8r">
          {cancelOpen ? <div data-id="element-3091" data-mp="99qwfyw5pov8">
              <p className="text-sm font-bold text-dark-navy" data-id="element-3092" data-mp="998r8crio0b6">
                Why are you cancelling this order?
              </p>
              <div className="flex flex-wrap gap-2 mt-3" data-id="element-3093" data-mp="4bz1ew07tmmt">
                {CANCEL_REASONS.map(r => <button key={r} onClick={() => setReason(r)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-150 ease-out ${reason === r ? 'border-primary-indigo bg-primary-indigo/[0.06] text-primary-indigo' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-3094" data-mp="ic032dob0y3c">
                    {r}
                  </button>)}
              </div>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note (optional)" className="w-full mt-3 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-3095" data-mp="x0nwadaogvq4" />
              <div className="flex items-center justify-end gap-2 mt-4" data-id="element-3096" data-mp="3a6ayw771edgw">
                <Ghost label="Keep order" onClick={() => setCancelOpen(false)} data-id="element-3097" data-mp="3a8f5iwwblqkv" />
                <button onClick={() => onCancel(reason, note)} className="px-5 py-2.5 rounded-xl bg-error text-white text-sm font-semibold hover:bg-red-600 transition-colors duration-150 ease-out" data-id="element-3098" data-mp="3e6uwfbvivj3k">
                  Cancel order
                </button>
              </div>
            </div> : confirmFulfil ? <div data-id="element-3099" data-mp="2vhy4henll2oi">
              <p className="text-sm font-bold text-dark-navy" data-id="element-3100" data-mp="2vig84wclaj6g">
                Mark this order as fulfilled?
              </p>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed" data-id="element-3101" data-mp="aqxmn419wd5r">
                This means the customer has received the product or the service
                has been completed.
              </p>
              <div className="flex items-center justify-end gap-2 mt-4" data-id="element-3102" data-mp="fw1f9bjyyl6g">
                <Ghost label="Cancel" onClick={() => setConfirmFulfil(false)} data-id="element-3103" data-mp="fxulwqul15eg" />
                <button onClick={() => onStatus('fulfilled')} className="px-5 py-2.5 rounded-xl bg-primary-indigo text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3104" data-mp="b3i2uhx0pw38">
                  Mark fulfilled
                </button>
              </div>
            </div> : <div className="flex items-center gap-2" data-id="element-3105" data-mp="3ocmgu7a9ubzj">
              {order.status === 'fulfilled' ? <div className="flex-1 min-w-0" data-id="element-3106" data-mp="3kb17ux0uyhum">
                  <p className="inline-flex items-center gap-1.5 text-sm font-bold text-success" data-id="element-3107" data-mp="3kcccez24ntms">
                    <CheckIcon className="w-4 h-4" data-id="element-3108" data-mp="1mhvm19gttncx" />
                    Fulfilled
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5 truncate" data-id="element-3109" data-mp="3c1byb0zw92oo">
                    {order.fulfilledAt}
                    {order.fulfilledBy ? ` · ${order.fulfilledBy}` : ''}
                  </p>
                </div> : order.status === 'cancelled' ? <p className="flex-1 text-sm font-semibold text-slate-400" data-id="element-3110" data-mp="32yklawrddyux">
                  This order was cancelled.
                </p> : external ? <div className="flex-1 min-w-0" data-id="element-3111" data-mp="3ut4qit752jvc">
                  <p className="text-sm font-bold text-dark-navy" data-id="element-3112" data-mp="3ljtqeir0n0j">
                    Managed by connected system
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5" data-id="element-3113" data-mp="3lq3nj1dkl8vw">
                    Status updates automatically · {order.lastSync}
                  </p>
                </div> : <>
                  <button onClick={() => setCancelOpen(true)} className="px-4 py-3 rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-50 hover:text-error transition-colors duration-150 ease-out" data-id="element-3114" data-mp="2aaez05ko41sz">
                    Cancel order
                  </button>
                  <div className="flex-1" data-id="element-3115" data-mp="22ujvvvny4wgn" />
                  {order.status === 'new' ? <button onClick={() => onStatus('progress')} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-indigo text-white text-sm font-bold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3116" data-mp="2gq5p6f17q2ev">
                      <PlayIcon className="w-4 h-4" data-id="element-3117" data-mp="1j2euynxd4mo2" />
                      Start order
                    </button> : <button onClick={() => setConfirmFulfil(true)} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-indigo text-white text-sm font-bold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3118" data-mp="1t25nwl69ytm1">
                      <CheckIcon className="w-4 h-4" data-id="element-3119" data-mp="evgqrzsbbqxq" />
                      Mark as fulfilled
                    </button>}
                </>}
              {external && order.status !== 'fulfilled' && <button onClick={() => onStatus('fulfilled')} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3120" data-mp="orjq6xvs5bgz">
                  <RefreshCwIcon className="w-3.5 h-3.5" data-id="element-3121" data-mp="17it3li8o8kdu" />
                  Override
                </button>}
            </div>}
        </footer>
      </motion.aside>
    </div>;
}
function Block({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="bg-white border border-slate-200 rounded-2xl p-5" data-id="element-3122" data-mp="1licay8tnrrp3">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3.5" data-id="element-3123" data-mp="360wjpnwadmvy">
        {title}
      </h3>
      {children}
    </section>;
}
function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-start justify-between gap-4" data-id="element-3124" data-mp="2x1j6ck51ke5y">
      <dt className="text-xs text-slate-400 flex-shrink-0" data-id="element-3125" data-mp="2s1n2qm09aoqx">{label}</dt>
      <dd className="text-xs font-semibold text-dark-navy text-right" data-id="element-3126" data-mp="2s3hq06z02sy6">
        {value}
      </dd>
    </div>;
}
function Action({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <button className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3127" data-mp="3fzcatqvbcw5g">
      <Icon className="w-3.5 h-3.5" data-id="element-3128" data-mp="cbajpj8ejkcy" />
      {label}
    </button>;
}
function Ghost({
  label,
  onClick
}: {
  label: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-3129" data-mp="p6r71obvcgwm">
      {label}
    </button>;
}